import React, { useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { GestureHandlerRootView, PanGestureHandler } from 'react-native-gesture-handler';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  useAnimatedGestureHandler,
  runOnJS,
} from 'react-native-reanimated';

import { scale, scaleVertically } from '../utils/scale.utils';
import { Color } from '../styles';

const RangeSlider = ({ sliderWidth, stopCount, onChange, initialValue }) => {
  const [value, setValue] = useState(initialValue);
  const stops = Array.from({ length: stopCount }, (_, index) => index + 1);
  const step = sliderWidth / stopCount;
  const position = useSharedValue(initialValue * step);

  const gestureHandler = useAnimatedGestureHandler({
    onStart: (_, ctx) => {
      ctx.startX = position.value;
    },
    onActive: (e, ctx) => {
      const thumbXPosition = ctx.startX + e.translationX;
      let newPosition;
      if (thumbXPosition < 0) {
        newPosition = 0;
      } else if (thumbXPosition > sliderWidth) {
        newPosition = sliderWidth;
      } else {
        newPosition = Math.round(thumbXPosition / step) * step;
      }
      position.value = newPosition;
      const newValue = Math.round(newPosition / step);
      if (newValue !== value) {
        runOnJS(setValue)(newValue);
        runOnJS(onChange)(newValue);
      }
    },
    onEnd: () => {},
  });

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: position.value }],
  }));
  const sliderStyle = useAnimatedStyle(() => ({
    width: position.value,
  }));

  return (
    <GestureHandlerRootView style={styles.sliderContainer}>
      <View style={[styles.sliderBack, { width: sliderWidth }]} />
      <Animated.View style={[styles.sliderFront, sliderStyle]} />
      <PanGestureHandler
        onGestureEvent={gestureHandler}
        hitSlop={{ top: 25, left: 50, right: 50, bottom: 25 }}
      >
        <Animated.View style={[styles.thumb, styles.thumbShadow, animatedStyle]}>
          <View style={styles.caret}></View>
          <View style={styles.overCaret}></View>
        </Animated.View>
      </PanGestureHandler>
      <View style={RangeSliderCommonStyles.bringToBack}>
        <View style={styles.ruleLine}></View>
        <View style={styles.ruleTextContainer}>
          <Text style={[styles.ruleText, styles.firstRuleText]}>Rate</Text>
        </View>
      </View>

      {stops.map((index) => {
        return (
          <View
            style={[{ left: step * index }, RangeSliderCommonStyles.bringToBack]}
            key={`stop-${index}`}
          >
            <View style={styles.ruleLine}></View>
            <View style={styles.ruleTextContainer}>
              <Text style={value === index ? styles.highlightedRuleText : styles.ruleText}>
                {index}
              </Text>
            </View>
          </View>
        );
      })}
    </GestureHandlerRootView>
  );
};

const RangeSliderCommonStyles = StyleSheet.create({
  bringToBack: {
    zIndex: -1,
  },
  caret: {
    position: 'absolute',
    left: -scale(1),
    borderLeftWidth: scale(6),
    borderRightWidth: scale(6),
    borderBottomWidth: scale(7),
    borderLeftColor: Color.transparent,
    borderRightColor: Color.transparent,
    transform: [{ translateY: scale(5) }, { rotate: '180deg' }],
  },
});
const styles = StyleSheet.create({
  sliderContainer: {
    justifyContent: 'center',
    alignSelf: 'center',
  },
  sliderBack: {
    height: scaleVertically(4),
    backgroundColor: Color.addReview.disabledRange,
  },
  sliderFront: {
    height: scaleVertically(4),
    backgroundColor: Color.addReview.primaryColor,
    position: 'absolute',
  },
  thumb: {
    position: 'absolute',
    left: -scale(10),
    width: scale(20),
    height: scale(20),
    backgroundColor: Color.addReview.primaryColor,
    borderColor: Color.addReview.primaryColor,
    borderWidth: scale(5),
    borderRadius: scale(3),
  },
  thumbShadow: {
    elevation: scale(6), // mobile shadow size
    // below shadow effect for web
    shadowOpacity: 0.3,
    shadowRadius: scale(6),
    shadowOffset: {
      width: scale(1),
      height: scale(2),
    },
  },
  caret: {
    ...RangeSliderCommonStyles.caret,
    top: -scale(2),
    borderBottomColor: Color.background,
  },
  // to obtain the caret symbol we are placing another style over the previous one
  overCaret: {
    ...RangeSliderCommonStyles.caret,
    top: -scale(4),
    borderBottomColor: Color.addReview.primaryColor,
  },
  ruleLine: {
    position: 'absolute',
    top: 0,
    bottom: -scale(11),
    left: -scale(1),
    backgroundColor: Color.addReview.disabledRange,
    width: scale(1),
    ...RangeSliderCommonStyles.bringToBack,
  },
  ruleTextContainer: {
    position: 'absolute',
    left: 0,
    top: scale(8),
  },
  firstRuleText: {
    left: -scale(10),
  },
  ruleText: {
    position: 'absolute',
    fontWeight: 'bold',
    left: -scale(4),
    fontSize: scale(10),
    top: scale(3),
    color: Color.addReview.ruleText,
  },
  highlightedRuleText: {
    position: 'absolute',
    fontWeight: 'bold',
    left: -scale(5),
    fontSize: scale(14),
    top: scale(1),
    color: Color.addReview.primaryColor,
  },
});

export default RangeSlider;
